/**
 * Pure recognizer for "the agent just wrote a note" — the chat surface's
 * NOTEBOOK-WRITE seam (frontend-doctrine §4.17).
 *
 * Notes are not announced over any dedicated channel: a note is created by an
 * ordinary `file_write` tool call whose `path` lands under the `notebook/`
 * vault prefix (claw-doctrine §2.1 dual-writer; TOOLS.md § Notebook). Those
 * tool calls ALREADY stream to the browser over `/ws/chat` and are persisted
 * verbatim on the assistant row (`messages.toolCalls[]`), and — the load-bearing
 * fact — **the complete file bytes ride in `args`**, e.g.
 *
 *   { "content": "---\ncreatedBy: agent\n---\n# Wedding Venues…",
 *     "path": "notebook/wedding-venues.md" }
 *
 * (verbatim from a real turn: `state/runtime-trace.jsonl` `tool_call_start`,
 * `tool: "file_write"`). So the chat surface can render the note itself with
 * NO extra fetch on the hot path — it works before any notebook list refresh,
 * and identically for a live turn and for reloaded history.
 *
 * Defensive by construction: `args` may arrive as a JSON string OR as a
 * structured object (`useChat` normalizes to a string at ingestion, but the
 * persisted rows predate that), key names are matched against a small alias
 * set, and ANY mismatch returns `null` rather than throwing — an unrecognized
 * write must degrade to "no widget", never to a broken transcript.
 *
 * Path handling is REJECT-only: the vault-relative remainder goes through the
 * existing `validateNotebookPath` choke point, so a traversal-shaped or
 * non-markdown path yields no widget and can never become a deep link.
 *
 * Pure: no React, no Convex, no fs, no `new Date()`.
 */

import { parseNoteDocument } from "./notebook-frontmatter";
import {
  NOTEBOOK_MOUNT_PATH,
  NOTEBOOK_PREFIX,
  titleFromPath,
  validateNotebookPath,
  type NotebookPath,
} from "./notebook-path";

/**
 * Whole-file write verbs that create/replace a note. Deliberately NOT
 * `file_edit`/`str_replace`: those carry a PATCH, not the file, so their args
 * cannot render a faithful preview — an edited note reconciles through the
 * ordinary notebook list read instead of a wrong-looking card.
 */
const WRITE_TOOL_NAMES: ReadonlySet<string> = new Set(["file_write"]);

/** Arg aliases, most-canonical first. `path`/`content` are what the pod sends. */
const PATH_KEYS = ["path", "file_path", "filename", "file"] as const;
const CONTENT_KEYS = ["content", "contents", "text", "body"] as const;

/** Lines of body shown before the card asks to be expanded. */
export const NOTE_PREVIEW_LINES = 12;

/** A recognized notebook write, ready to render. */
export interface NotebookWriteEvent {
  /** Vault-relative note path — the notebook surface's identity (`about-you.md`). */
  readonly path: NotebookPath;
  /** Display title: frontmatter `title:` when present, else the filename. */
  readonly title: string;
  /** The note body with any frontmatter block stripped (never the raw YAML). */
  readonly body: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Accept both wire shapes: a JSON string, or an already-structured object. */
function coerceArgs(args: unknown): Record<string, unknown> | null {
  if (typeof args === "string") {
    try {
      const parsed: unknown = JSON.parse(args);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return isRecord(args) ? args : null;
}

function firstString(
  record: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return null;
}

/**
 * Reduce a written path to its vault-relative form, or `null` when the write
 * did not target the notebook. Accepts the pod-absolute mount path and the
 * workspace-relative form (with or without a `./` lead) — the two shapes the
 * agent is taught in TOOLS.md and BOOTSTRAP.md respectively.
 */
function toVaultRelativePath(rawPath: string): string | null {
  const trimmed = rawPath.trim();

  const mounted = `${NOTEBOOK_MOUNT_PATH}/`;
  if (trimmed.startsWith(mounted)) return trimmed.slice(mounted.length);

  const relative = trimmed.startsWith("./") ? trimmed.slice(2) : trimmed;
  if (relative.startsWith(NOTEBOOK_PREFIX)) {
    return relative.slice(NOTEBOOK_PREFIX.length);
  }
  return null;
}

/**
 * Recognize a single tool call as a notebook write. Returns `null` for every
 * non-match — wrong verb, unparseable args, missing keys, a path outside the
 * vault, or a path the notebook domain rejects.
 */
export function parseNotebookWrite({
  toolName,
  args,
}: {
  toolName: string;
  args: unknown;
}): NotebookWriteEvent | null {
  if (typeof toolName !== "string") return null;
  if (!WRITE_TOOL_NAMES.has(toolName.trim().toLowerCase())) return null;

  const record = coerceArgs(args);
  if (record === null) return null;

  const rawPath = firstString(record, PATH_KEYS);
  const rawContent = firstString(record, CONTENT_KEYS);
  if (rawPath === null || rawContent === null) return null;

  const relative = toVaultRelativePath(rawPath);
  if (relative === null) return null;

  const validated = validateNotebookPath({ path: relative });
  if (validated.isErr()) return null;
  const path = validated.value;

  // Frontmatter is advisory and the parse is TOTAL — a garbled block degrades
  // to "the whole thing is body", which still previews honestly.
  const { frontmatter, body } = parseNoteDocument({ raw: rawContent });
  const declaredTitle = frontmatter.title?.trim() ?? "";

  return {
    path,
    title: declaredTitle.length > 0 ? declaredTitle : titleFromPath({ path }),
    body,
  };
}

/**
 * Recognize every notebook write in a turn's tool calls, newest content wins
 * per path. An agent that writes a note and then rewrites it in the same turn
 * gets ONE card carrying the final bytes — never a stack of stale duplicates.
 */
export function collectNotebookWrites(
  tools: readonly { name: string; args: string }[],
): NotebookWriteEvent[] {
  const byPath = new Map<string, NotebookWriteEvent>();
  for (const tool of tools) {
    const note = parseNotebookWrite({ toolName: tool.name, args: tool.args });
    if (note !== null) byPath.set(note.path, note);
  }
  return [...byPath.values()];
}

/**
 * Clip a note body to its opening lines for the collapsed card. Trailing
 * whitespace is dropped first so a file ending in blank lines doesn't report
 * itself as truncated.
 */
export function takeNotePreview({
  body,
  maxLines = NOTE_PREVIEW_LINES,
}: {
  body: string;
  maxLines?: number;
}): { preview: string; truncated: boolean } {
  const lines = body.replace(/\s+$/, "").split("\n");
  if (lines.length <= maxLines) {
    return { preview: lines.join("\n"), truncated: false };
  }
  return { preview: lines.slice(0, maxLines).join("\n"), truncated: true };
}
